/**
 * Chat API — wraps the generated admin-bff chat client.
 *
 * The generated DTOs use opaque `{ [key: string]: unknown }` data fields because
 * the BFF proxies upstream chat-manager responses verbatim. We normalise those here.
 *
 * All functions now use the typed generated client — no raw adminBffFetch calls.
 */

import {
  adminBffFetch,
  closeChatSession,
  createChatSession,
  deleteChatSession,
  getChatSession,
  listChatMessages,
  listChatSessions,
  sendChatMessage,
} from '@avila-roffe/api-clients-admin-bff';
import type { ContentBlock } from '@avila-roffe/components-web';

// ─── Domain types ─────────────────────────────────────────────────────────────

export type SessionStatus = 'active' | 'closed' | 'error';

export interface ChatSession {
  id: string;
  tenantSlug: string;
  agentName: string;
  agentAppSlug: string;
  title: string;
  status: SessionStatus;
  /** ISO datetime string */
  lastActivityAt: string;
  createdAt: string;
  messageCount?: number;
}

export type MessageRole = 'user' | 'assistant' | 'system';

export interface ChatMessage {
  id: string;
  sessionId: string;
  role: MessageRole;
  content: string;
  /**
   * Structured content blocks. When present the frontend renders these instead
   * of the plain `content` string. Populated by go-chat-manager when the agent
   * response contains structured block JSON; falls back to `content` when absent.
   */
  blocks?: ContentBlock[];
  createdAt: string;
  /** Agent name — populated in MULTI_AGENT sessions where multiple agents may respond. */
  agentName?: string;
}

export interface CreateSessionPayload {
  agentName: string;
  agentAppSlug: string;
  title?: string;
  mode?: 'SINGLE_AGENT' | 'MULTI_AGENT' | 'GROUP';
  /** Agent config overrides — merged server-side on top of the live agent config. */
  agentConfig?: Record<string, unknown>;
}

// ─── Normalisation helpers ────────────────────────────────────────────────────

function toRecord(v: unknown): Record<string, unknown> {
  return typeof v === 'object' && v !== null ? (v as Record<string, unknown>) : {};
}

function toDisplayName(name: string): string {
  if (!name) return name;
  return name
    .split(/[-_\s]+/)
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : ''))
    .join(' ');
}

function toStatus(v: unknown): SessionStatus {
  const s = typeof v === 'string' ? v.toLowerCase() : '';
  if (s === 'active') return 'active';
  if (s === 'closed' || s === 'archived') return 'closed';
  if (s === 'error') return 'error';
  return 'active';
}

function normaliseSession(raw: unknown): ChatSession {
  const r = toRecord(raw);
  const title = r.title != null ? String(r.title) : '';
  return {
    id: String(r.id ?? ''),
    tenantSlug: String(r.tenantSlug ?? r.tenant_slug ?? ''),
    agentName: toDisplayName(String(r.agentName ?? r.agent_name ?? '')),
    agentAppSlug: String(r.agentAppSlug ?? r.agent_app_slug ?? ''),
    title,
    status: toStatus(r.status),
    lastActivityAt: String(
      r.lastMessageAt ??
        r.lastActivityAt ??
        r.last_activity_at ??
        r.updatedAt ??
        r.createdAt ??
        new Date(0).toISOString(),
    ),
    createdAt: String(r.createdAt ?? r.created_at ?? new Date(0).toISOString()),
    messageCount: typeof r.totalMessages === 'number' ? r.totalMessages : undefined,
  };
}

function parseBlocks(raw: unknown): ContentBlock[] | undefined {
  if (!Array.isArray(raw) || raw.length === 0) return undefined;
  return raw as ContentBlock[];
}

function normaliseMessage(raw: unknown): ChatMessage {
  const r = toRecord(raw);
  const role =
    r.role === 'user' || r.role === 'assistant' || r.role === 'system' ? r.role : 'assistant';
  return {
    id: String(r.id ?? ''),
    sessionId: String(r.sessionId ?? r.session_id ?? ''),
    role,
    content: String(r.content ?? ''),
    blocks: parseBlocks(r.blocks),
    createdAt: String(r.createdAt ?? r.created_at ?? new Date(0).toISOString()),
    agentName: r.agentName != null ? String(r.agentName) : undefined,
  };
}

function extractRows(payload: unknown): unknown[] {
  if (Array.isArray(payload)) return payload;
  const root = toRecord(payload);
  const d1 = root.data;
  if (Array.isArray(d1)) return d1;
  const r1 = toRecord(d1);
  const d2 = r1.data;
  if (Array.isArray(d2)) return d2;
  const r2 = toRecord(d2);
  if (Array.isArray(r2.items)) return r2.items as unknown[];
  if (Array.isArray(r1.items)) return r1.items as unknown[];
  if (Array.isArray(r1.messages)) return r1.messages as unknown[];
  if (Array.isArray(r2.messages)) return r2.messages as unknown[];
  return [];
}

// ─── Public API ───────────────────────────────────────────────────────────────

export async function fetchSessions(): Promise<ChatSession[]> {
  const res = await listChatSessions();
  return extractRows(res.data).map(normaliseSession);
}

export async function fetchSession(id: string): Promise<ChatSession | null> {
  const res = await getChatSession(id);
  const root = toRecord(res);
  const data = toRecord(root.data);
  if (!data.id) return null;
  return normaliseSession(data.data ?? data);
}

export async function closeSession(id: string): Promise<ChatSession | null> {
  const res = await closeChatSession(id);
  const root = toRecord(res);
  const data = toRecord(root.data);
  return normaliseSession(data.data ?? data);
}

export async function deleteSession(id: string): Promise<void> {
  await deleteChatSession(id);
}

export async function createSession(payload: CreateSessionPayload): Promise<ChatSession> {
  // Map UI mode ('SINGLE_AGENT') to backend mode ('DIRECT') at the API boundary.
  const backendMode = payload.mode === 'SINGLE_AGENT' || !payload.mode ? 'DIRECT' : payload.mode;
  const res = await createChatSession({
    agentName: payload.agentName,
    agentAppSlug: payload.agentAppSlug,
    title: payload.title ?? `Session with ${payload.agentName}`,
    mode: backendMode,
    ...(payload.agentConfig ? { agentConfig: payload.agentConfig } : {}),
  });
  const root = toRecord(res);
  const data = toRecord(root.data);
  return normaliseSession(data.data ?? data);
}

export async function fetchMessages(sessionId: string): Promise<ChatMessage[]> {
  const res = await listChatMessages(sessionId);
  return extractRows(res).map(normaliseMessage);
}

export async function sendMessage(sessionId: string, content: string): Promise<ChatMessage> {
  const res = await sendChatMessage(sessionId, { role: 'user', content });
  const root = toRecord(res);
  const data = toRecord(root.data);
  return normaliseMessage(data.data ?? data);
}

export async function updateSession(id: string, patch: { title?: string }): Promise<ChatSession> {
  const res = await adminBffFetch<unknown>(`/chat/sessions/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  const root = toRecord(res);
  const data = toRecord(root.data);
  return normaliseSession(data.data ?? data);
}
